export interface FailTimelineItem {
  time: string;
  text: string;
}

export interface FailScreenshot {
  url: string;
  alt: string;
  caption: string;
}

export interface FailReaction {
  user: string;
  text: string;
  likes: number;
}

export interface FailSection {
  heading: string;
  paragraphs: string[];
}

export interface FailArticle {
  id: string;
  title: string;
  category: string;
  categorySlug: string;
  tags: string[];
  author: string;
  date: string;
  views: string;
  readTime: string;
  failIndex: number;
  cover: string;
  excerpt: string;
  lead: string;
  quote: string;
  timeline: FailTimelineItem[];
  screenshots: FailScreenshot[];
  sections: FailSection[];
  reactions: FailReaction[];
}

export const articles: FailArticle[] = [
  {
    id: "live-price-meltdown",
    title: "把原价说成骨折价：一场直播报价失误是怎么滚成热搜的",
    category: "带货翻车",
    categorySlug: "sales-fail",
    tags: ["带货失误", "报价事故", "直播事故"],
    author: "观察局编辑 林知夏",
    date: "2026-08-28",
    views: "128.4 万",
    readTime: "6 分钟",
    failIndex: 9,
    cover: "https://readdy.ai/api/search-image?query=cluttered%20livestream%20selling%20desk%20with%20scattered%20cosmetic%20boxes%20and%20fallen%20products%2C%20warm%20orange%20studio%20lighting%2C%20empty%20chair%2C%20no%20people%2C%20editorial%20photography%2C%20shallow%20depth%20of%20field&width=1200&height=750&seq=fail-cover-01&orientation=landscape",
    excerpt: "镜头前的主播把一款护肤套装的价格念错了整整两位，后台改价来不及，三分钟涌入四万单。这大概是今年最典型的一次带货报价事故。",
    lead: "直播间里的价格，从来不是一句话的事。它牵涉到库存、后台改价、优惠券叠加和主播的口播节奏。当其中一个环节脱节，翻车就开始了。",
    quote: "「所有价格以页面为准」这句免责口播，成了当晚被截图最多的一句。",
    timeline: [
      { time: "20:14", text: "主播在讲解第三款商品时，口播价格比实际售价低了近六成。" },
      { time: "20:16", text: "直播间评论区出现大量「已拍」截图，后台订单量陡增。" },
      { time: "20:19", text: "助播试图用「以页面价格为准」圆场，但节奏已经完全乱掉。" },
      { time: "20:31", text: "商品链接被紧急下架，主播宣布「刚才那波不算」，弹幕彻底炸开。" },
      { time: "次日 09:00", text: "相关话题进入热搜，官方账号发布说明并给出补偿方案。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=blurred%20mobile%20screen%20glow%20on%20dark%20background%20with%20abstract%20shopping%20interface%20shapes%2C%20stylized%2C%20no%20text%2C%20warm%20orange%20tint&width=800&height=800&seq=fail-shot-01a&orientation=squarish",
        alt: "直播带货报价事故相关画面示意",
        caption: "订单量在改价前出现的陡增曲线示意",
      },
      {
        url: "https://readdy.ai/api/search-image?query=empty%20livestream%20studio%20desk%20with%20turned%20off%20ring%20light%20and%20scattered%20product%20samples%2C%20dim%20warm%20light%2C%20moody%2C%20no%20people&width=800&height=800&seq=fail-shot-01b&orientation=squarish",
        alt: "直播结束后空置的带货工作台",
        caption: "次日凌晨，直播间内已停止讲解的带货工作台",
      },
    ],
    sections: [
      {
        heading: "报价失误为什么杀伤力这么大",
        paragraphs: [
          "在直播电商里，价格以「口播 + 页面改价」的方式同步落地。主播负责说，运营负责改，两者之间通常只有几十秒的窗口。一旦口播价格低于实际售价，用户下单速度会远超运营手速。",
          "这次的失误不在于价格本身，而在于纠正的时机。翻车发生在纠正动作被弹幕淹没的那一刻。",
        ],
      },
      {
        heading: "补救动作决定了舆情走向",
        paragraphs: [
          "团队当晚给出的方案是「已下单用户按口播价履约，差额由品牌承担」，同时公布补货时间。这个处理在评论区获得了相当比例的认可，也让事件从「割韭菜」转向「真翻车但敢认」。",
          "对比同类事件，愿意按错误价格履约的团队，后续口碑回落速度明显更快。",
        ],
      },
    ],
    reactions: [
      { user: "柠檬气泡不加冰", text: "该说不说，敢按口播价发货这点还是可以的。", likes: 2841 },
      { user: "南门修车铺", text: "运营手速跟不上的时候，最好的办法是先闭麦。", likes: 1633 },
      { user: "半糖去冰", text: "截图已经存好了，明天看看补偿方案能不能跟上。", likes: 902 },
    ],
  },
  {
    id: "live-mic-off-air",
    title: "忘关麦三分钟：一段被完整记录的直播事故",
    category: "直播事故",
    categorySlug: "live-accident",
    tags: ["直播事故", "忘关麦", "现场回放"],
    author: "观察局编辑 周砚",
    date: "2026-08-21",
    views: "96.7 万",
    readTime: "5 分钟",
    failIndex: 8,
    cover: "https://readdy.ai/api/search-image?query=fallen%20ring%20light%20and%20tangled%20cables%20on%20wooden%20floor%2C%20dramatic%20red%20glow%20from%20side%2C%20dark%20moody%20studio%2C%20no%20people%2C%20editorial%20still%20life&width=1200&height=750&seq=fail-cover-02&orientation=landscape",
    excerpt: "导播切走了画面，声音却留在了直播间。三分钟后，全网开始逐字转写这段对话。",
    lead: "直播事故的经典结构是：画面走了，声音没走。观众听得到，却看不到，于是截图和转写就成了唯一的证据。",
    quote: "关麦这件事，永远是想起来的时候已经晚了。",
    timeline: [
      { time: "21:02", text: "主画面切到产品特写，导播示意进入中场休息。" },
      { time: "21:04", text: "麦克风被误判为已关闭，主播与场控的对话继续被直播出去。" },
      { time: "21:06", text: "弹幕开始密集刷屏提示，但场控没有同步监看弹幕。" },
      { time: "21:09", text: "声音被切断，直播间转入静音画面。" },
      { time: "当晚", text: "多位观众发布逐字转写，话题进入讨论榜前十。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=close%20up%20of%20condenser%20microphone%20on%20dark%20desk%20with%20red%20indicator%20light%2C%20warm%20moody%20studio%20background%2C%20no%20people&width=800&height=800&seq=fail-shot-02a&orientation=squarish",
        alt: "直播麦克风特写示意",
        caption: "仍然处于开启状态的麦克风指示灯",
      },
      {
        url: "https://readdy.ai/api/search-image?query=blurred%20monitor%20wall%20with%20many%20dim%20screens%20in%20dark%20control%20room%2C%20amber%20reflections%2C%20stylized%2C%20no%20text&width=800&height=800&seq=fail-shot-02b&orientation=squarish",
        alt: "导播间监看屏幕示意",
        caption: "导播间中未被实时监看的弹幕信号",
      },
    ],
    sections: [
      {
        heading: "「静音」需要的是一个确认动作",
        paragraphs: [
          "很多团队把静音交给了设备指示灯，而不是固定的确认流程。指示灯会有延迟，也会被遮挡，更需要有人负责二次确认。",
          "这次事故里，最有价值的教训其实是：弹幕应该由一个人专门盯。如果当时有人看弹幕，至少能提前两分钟止损。",
        ],
      },
      {
        heading: "为什么观众对这类事故格外宽容",
        paragraphs: [
          "与报价、售后类失误不同，忘关麦往往不直接损害消费者利益，因此舆论更多停留在「好笑」而不是「愤怒」。",
          "但一旦直播内容涉及他人隐私，性质就会变化，这也是团队后续公开致歉的原因。",
        ],
      },
    ],
    reactions: [
      { user: "海盐可可", text: "全程听完只想说，关麦按钮真的会咬人。", likes: 3120 },
      { user: "老张的猫", text: "建议所有直播间加一个『声音已关闭』的二次提示。", likes: 1450 },
      { user: "十七号观众", text: "能第一时间道歉还算体面。", likes: 733 },
    ],
  },
  {
    id: "sales-number-mixup",
    title: "库存说成十万件：一句口播引发的抢购与退款潮",
    category: "带货翻车",
    categorySlug: "sales-fail",
    tags: ["带货失误", "库存口播", "退款潮"],
    author: "观察局编辑 沈蔚",
    date: "2026-08-14",
    views: "74.2 万",
    readTime: "4 分钟",
    failIndex: 7,
    cover: "https://readdy.ai/api/search-image?query=cardboard%20shipping%20boxes%20tumbling%20off%20a%20warehouse%20shelf%2C%20warm%20industrial%20lighting%2C%20motion%20blur%2C%20no%20people%2C%20editorial%20photography&width=1200&height=750&seq=fail-cover-05&orientation=landscape",
    excerpt: "库存数字被念成了实际数量的二十倍。抢购来的很快，退款来得更快。",
    lead: "当口播的库存数量和后台真实库存脱节，用户买到的是「预期」，而不是「货」。",
    quote: "直播间里最能刺激下单的两个词：最后一批。",
    timeline: [
      { time: "19:40", text: "主播在催单环节称该款库存「还有十万件」，实际可用库存约五千。" },
      { time: "19:44", text: "链接售罄，大量用户未能下单，评论区出现质疑。" },
      { time: "19:52", text: "后台放出预售链接，承诺七天内发货。" },
      { time: "第三日", text: "部分用户因等待时间过长申请退款，退款比例接近三成。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=pile%20of%20plain%20cardboard%20boxes%20stacked%20in%20warm%20lit%20warehouse%2C%20soft%20shadows%2C%20no%20people%2C%20clean%20composition&width=800&height=800&seq=fail-shot-03a&orientation=squarish",
        alt: "仓库待发货物示意",
        caption: "预售承诺期内的待发货物堆放区",
      },
    ],
    sections: [
      {
        heading: "库存口播的边界在哪里",
        paragraphs: [
          "合理的做法是只口播「是否有货」和「是否限量」，具体数字交给页面。因为页面可以随时同步，口播不能。",
          "夸张的库存数字短期内确实能提升转化，但当发货周期拉长，退货和差评会把这部分收益吃掉。",
        ],
      },
    ],
    reactions: [
      { user: "阿茶不加糖", text: "等了一周还没发货，退款了。", likes: 1870 },
      { user: "数码老李", text: "库存数字这种话真的别乱说。", likes: 1122 },
    ],
  },
  {
    id: "connection-chaos-link",
    title: "连线半小时听不见对方：一次典型的连线失控全记录",
    category: "连线失控",
    categorySlug: "connection-chaos",
    tags: ["连线失控", "设备故障", "直播事故"],
    author: "观察局编辑 周砚",
    date: "2026-08-06",
    views: "61.9 万",
    readTime: "5 分钟",
    failIndex: 6,
    cover: "https://readdy.ai/api/search-image?query=blurred%20motion%20of%20stage%20spotlights%20over%20an%20empty%20podium%2C%20abstract%20warm%20amber%20and%20red%20light%20trails%2C%20no%20people%2C%20cinematic&width=1200&height=750&seq=fail-cover-08&orientation=landscape",
    excerpt: "「能听到吗？」说了十七次。这是连线类直播最常见的翻车模板。",
    lead: "连线直播有两个不可控变量：对方的网络，和对方的环境。这两样都不在你的机房里。",
    quote: "「你先说，你先说。」——双方同时沉默的经典瞬间。",
    timeline: [
      { time: "20:10", text: "连线建立，对方画面正常但声音中断。" },
      { time: "20:13", text: "主播开始反复询问「能听到吗」，弹幕刷起同一句话。" },
      { time: "20:24", text: "双方同时开口，然后同时停下，直播出现近二十秒的空白。" },
      { time: "20:39", text: "连线被切断，改为评论区文字问答。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20split%20screen%20video%20call%20frame%20with%20empty%20seats%20and%20dim%20warm%20lighting%2C%20stylized%2C%20no%20people%2C%20no%20text&width=800&height=800&seq=fail-shot-04a&orientation=squarish",
        alt: "双人连线画面示意",
        caption: "连线画面中双方同时沉默的空白片段",
      },
    ],
    sections: [
      {
        heading: "连线事故的止损流程",
        paragraphs: [
          "成熟团队会预先约定一个手势或暗号，一旦出现超过十秒的沟通空白，立刻切备用环节，而不是继续在镜头前「喂喂喂」。",
          "提前准备二十分钟的备用内容，比临场硬撑更能保住观众留存。",
        ],
      },
    ],
    reactions: [
      { user: "小猫钓鱼", text: "这段空白我看了三遍，太真实了。", likes: 1408 },
      { user: "路人甲已上线", text: "切备用环节真的应该写进流程。", likes: 690 },
    ],
  },
  {
    id: "speech-slip-apology",
    title: "一句话得罪一整个行业：言论翻车后的四十八小时",
    category: "言论翻车",
    categorySlug: "speech-fail",
    tags: ["言论翻车", "危机公关", "热搜"],
    author: "观察局编辑 林知夏",
    date: "2026-07-29",
    views: "88.1 万",
    readTime: "7 分钟",
    failIndex: 8,
    cover: "https://readdy.ai/api/search-image?query=cracked%20smartphone%20screen%20glowing%20on%20a%20tripod%20in%20a%20dark%20studio%2C%20warm%20red%20reflection%2C%20no%20people%2C%20moody%20editorial%20still%20life&width=1200&height=750&seq=fail-cover-03&orientation=landscape",
    excerpt: "从一句随口点评，到需要连发两条声明，中间只隔了一个晚上。",
    lead: "言论类翻车最麻烦的地方在于：内容会脱离语境传播。剪辑掉的上下句，往往才是原本的意思。",
    quote: "有些话在饭桌上没问题，在直播间里就是公开表达。",
    timeline: [
      { time: "第 1 天 21:30", text: "直播中一句行业点评被单独剪出，开始在短视频平台扩散。" },
      { time: "第 1 天 23:50", text: "相关话题讨论量突破十万，多个从业者公开发声回应。" },
      { time: "第 2 天 08:00", text: "本人账号发布第一条说明，强调「被断章取义」，效果不佳。" },
      { time: "第 2 天 15:00", text: "发布完整版直播片段并提供致歉，讨论热度开始回落。" },
      { time: "第 3 天", text: "合作品牌陆续恢复排期，事件进入长尾讨论。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20blurred%20video%20editing%20timeline%20on%20dark%20screen%20with%20warm%20orange%20waveform%2C%20stylized%2C%20no%20text%2C%20no%20people&width=800&height=800&seq=fail-shot-05a&orientation=squarish",
        alt: "短视频剪辑时间线示意",
        caption: "被单独截取传播的片段在剪辑线上的位置",
      },
      {
        url: "https://readdy.ai/api/search-image?query=empty%20microphone%20on%20a%20stand%20in%20dim%20warm%20room%20with%20soft%20window%20light%2C%20calm%20mood%2C%20no%20people&width=800&height=800&seq=fail-shot-05b&orientation=squarish",
        alt: "安静下来的直播间示意",
        caption: "第二条说明发布后的直播间",
      },
    ],
    sections: [
      {
        heading: "为什么第一条声明总是失效",
        paragraphs: [
          "「被断章取义」是一个辩解，不是一个回应。公众往往先关心感受，再关心事实。在情绪最高点先讲事实，很容易被理解为不认错。",
          "有效的顺序通常是：先承认表达带来的影响，再补充完整语境，最后给出具体行动。",
        ],
      },
      {
        heading: "品牌方的四十八小时",
        paragraphs: [
          "多数合作品牌在事发后选择暂停排期观察，而不是立刻解约。决定是否恢复的关键，是当事人后续的沟通态度和讨论量的回落速度。",
        ],
      },
    ],
    reactions: [
      { user: "风里有沙", text: "第一条声明如果想清楚再发，就不会有第二条了。", likes: 4230 },
      { user: "小满未满", text: "完整版放出来之后确实好理解很多。", likes: 2105 },
      { user: "隔壁老王", text: "直播说话真的要过脑子。", likes: 1588 },
    ],
  },
  {
    id: "persona-off-script",
    title: "人设崩在了一通电话上：从甜妹到翻车的十二小时",
    category: "人设崩塌",
    categorySlug: "persona-collapse",
    tags: ["人设崩塌", "连线翻车", "热搜现场"],
    author: "观察局编辑 沈蔚",
    date: "2026-07-18",
    views: "103.5 万",
    readTime: "6 分钟",
    failIndex: 9,
    cover: "https://readdy.ai/api/search-image?query=half%20broken%20neon%20sign%20flickering%20on%20a%20brick%20wall%2C%20warm%20pink%20and%20amber%20glow%2C%20moody%20night%20atmosphere%2C%20no%20people%2C%20no%20text&width=1200&height=750&seq=fail-cover-06&orientation=landscape",
    excerpt: "镜头外的语气，通过一通没挂断的电话传了出来。人设和真人之间的差距，才是真正的翻车点。",
    lead: "人设是内容的一部分，但当镜头外的表现与镜头内长期矛盾，观众会产生被欺骗的感觉——这才是最难挽回的部分。",
    quote: "观众可以接受你有脾气，不能接受你演的。",
    timeline: [
      { time: "第 1 天 22:40", text: "直播中接听工作电话，未关闭收音，语气与镜头形象反差明显。" },
      { time: "第 1 天 23:10", text: "录屏片段被发布，两小时内转发过万。" },
      { time: "第 2 天 02:00", text: "账号关闭评论，未作回应。" },
      { time: "第 2 天 10:30", text: "发布长文说明，承认此前内容存在「过度包装」。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=off%20hook%20telephone%20on%20a%20wooden%20desk%20next%20to%20a%20dim%20ring%20light%2C%20warm%20amber%20evening%20light%2C%20no%20people&width=800&height=800&seq=fail-shot-06a&orientation=squarish",
        alt: "未挂断的电话与补光灯示意",
        caption: "收音未关闭时，桌面上仍在通话的听筒",
      },
    ],
    sections: [
      {
        heading: "人设崩塌和言论翻车不是一回事",
        paragraphs: [
          "言论翻车针对的是「说了什么」，人设崩塌针对的是「一直在扮演谁」。后者的修复周期通常以年计算。",
          "承认包装过度是相对有效的一步，但它也会削弱后续所有人设内容的可信度，属于两难选择。",
        ],
      },
      {
        heading: "内容创作者的长期风险",
        paragraphs: [
          "越依赖单一标签的账号，抗风险能力越弱。多元内容结构虽然短期涨粉慢，但摔下来的时候不会那么重。",
        ],
      },
    ],
    reactions: [
      { user: "橘子汽水冒泡", text: "其实大家也没要求完美，只是别演。", likes: 5120 },
      { user: "深夜便利店", text: "这个反差看完整个人都清醒了。", likes: 3390 },
      { user: "阿麦", text: "长文写得还行，就看后面怎么做了。", likes: 1204 },
    ],
  },
  {
    id: "classic-scene-return",
    title: "被反复回放的五段名场面：它们为什么能火这么久",
    category: "名场面回放",
    categorySlug: "classic-moments",
    tags: ["名场面回放", "翻车瞬间", "经典片段"],
    author: "观察局编辑 苏晓",
    date: "2026-07-09",
    views: "145.8 万",
    readTime: "8 分钟",
    failIndex: 7,
    cover: "https://readdy.ai/api/search-image?query=skincare%20bottles%20spilled%20across%20a%20clean%20white%20table%20with%20a%20water%20puddle%2C%20soft%20studio%20lighting%2C%20warm%20tones%2C%20no%20people%2C%20minimal%20composition&width=1200&height=750&seq=fail-cover-07&orientation=landscape",
    excerpt: "一段翻车片段能活多久，取决于它有多容易被复述。我们整理了五个最常被提起的模板。",
    lead: "名场面之所以成为名场面，往往不是因为事故多大，而是因为它的结构足够简洁、足够好模仿。",
    quote: "能被二创的翻车，才会被记住。",
    timeline: [
      { time: "第一步", text: "瞬间：一个明确的反差动作或一句被截断的话。" },
      { time: "第二步", text: "扩散：录屏在两小时内完成第一轮传播。" },
      { time: "第三步", text: "固化：出现固定的表情包、音效或口播模仿。" },
      { time: "第四步", text: "长尾：数月后仍会被当作类比句式引用。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=grid%20of%20blurred%20video%20thumbnails%20on%20dark%20background%2C%20abstract%20warm%20amber%20glow%2C%20stylized%2C%20no%20text%2C%20no%20people&width=800&height=800&seq=fail-shot-07a&orientation=squarish",
        alt: "名场面片段合集示意",
        caption: "被反复剪辑传播的片段缩略图墙",
      },
    ],
    sections: [
      {
        heading: "第一段：突然的安静",
        paragraphs: [
          "最经典的模板是「全员安静三秒」。它不需要任何台词，观众自己会脑补气氛，所以特别适合二创。",
        ],
      },
      {
        heading: "第二段：把话说反了",
        paragraphs: [
          "口误类片段的传播力来自复述成本极低——你只需要学一句话，就能现场还原。",
        ],
      },
      {
        heading: "第三段：道具失控",
        paragraphs: [
          "产品在镜头前掉落、喷溅、散架，属于纯物理喜剧，语言不通也看得懂。",
        ],
      },
    ],
    reactions: [
      { user: "爆米花大份", text: "第三段我能循环一整天。", likes: 6203 },
      { user: "月亮邮差", text: "总结得很到位，能被模仿才活得久。", likes: 2890 },
      { user: "一只企鹅", text: "建议做成合集，吃饭时看。", likes: 1740 },
    ],
  },
  {
    id: "live-script-leak",
    title: "提词器反射进镜头：剧本是怎么被观众看见的",
    category: "直播事故",
    categorySlug: "live-accident",
    tags: ["直播事故", "提词器", "现场回放"],
    author: "观察局编辑 苏晓",
    date: "2026-06-30",
    views: "52.3 万",
    readTime: "4 分钟",
    failIndex: 5,
    cover: "https://readdy.ai/api/search-image?query=scattered%20paper%20documents%20and%20a%20contract%20on%20a%20desk%20under%20a%20warm%20desk%20lamp%2C%20soft%20shadows%2C%20no%20people%2C%20editorial%20still%20life&width=1200&height=750&seq=fail-cover-09&orientation=landscape",
    excerpt: "玻璃反光里那几行字，让一场即兴分享变成了念稿。",
    lead: "直播中的反光是最容易被忽略的穿帮来源：显示器、玻璃桌、水杯，甚至观众的眼镜。",
    quote: "真正的即兴，是不需要反光的。",
    timeline: [
      { time: "20:22", text: "机位调整后，提词器文字在玻璃桌面形成清晰倒影。" },
      { time: "20:25", text: "弹幕开始提示「屏幕反射」，主播未察觉。" },
      { time: "20:40", text: "有观众截取倒影区域并放大发布。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=reflection%20of%20blurred%20screen%20light%20on%20a%20glossy%20dark%20table%20surface%2C%20warm%20amber%20tint%2C%20abstract%2C%20no%20text%2C%20no%20people&width=800&height=800&seq=fail-shot-08a&orientation=squarish",
        alt: "桌面反光中的屏幕内容示意",
        caption: "被放大处理后可见的提词器倒影区域",
      },
    ],
    sections: [
      {
        heading: "穿帮检查清单",
        paragraphs: [
          "开播前扫一遍反光面：桌面、显示器边框、水杯、背景玻璃。最简单的方法是用手机从机位角度拍一张照片，反光位置一目了然。",
        ],
      },
    ],
    reactions: [
      { user: "键盘侠退休了", text: "以后开播前先拍一张检查反光，学到了。", likes: 980 },
      { user: "西瓜太郎", text: "这个还是挺常见的，不怪主播。", likes: 512 },
    ],
  },
  {
    id: "sales-sample-swap",
    title: "样品和发货货不对板：一场拖了两个月的售后",
    category: "带货翻车",
    categorySlug: "sales-fail",
    tags: ["带货翻车", "货不对板", "售后"],
    author: "观察局编辑 沈蔚",
    date: "2026-06-18",
    views: "67.4 万",
    readTime: "6 分钟",
    failIndex: 8,
    cover: "https://readdy.ai/api/search-image?query=microphone%20dropped%20on%20a%20theatre%20stage%20floor%20with%20a%20strong%20spotlight%20beam%2C%20dramatic%20contrast%2C%20no%20people%2C%20cinematic%20still&width=1200&height=750&seq=fail-cover-04&orientation=landscape",
    excerpt: "直播间的样品和用户收到的货差了一个档位，投诉持续了整整八周。",
    lead: "「样品即实物」是带货的基本承诺。一旦被打破，问题会从质量争议升级为信任争议。",
    quote: "嘴上说一件，手里拿一件，箱子发第三件。",
    timeline: [
      { time: "第 1 周", text: "首批用户收到商品，反馈与直播展示存在明显差异。" },
      { time: "第 2 周", text: "客服以「批次不同」回应，投诉量上升。" },
      { time: "第 4 周", text: "第三方对比测评视频发布，播放量迅速攀升。" },
      { time: "第 8 周", text: "发布统一退换方案，事件逐步平息。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=two%20plain%20product%20containers%20side%20by%20side%20on%20a%20neutral%20warm%20backdrop%2C%20comparison%20still%20life%2C%20soft%20studio%20light%2C%20no%20text%2C%20no%20people&width=800&height=800&seq=fail-shot-09a&orientation=squarish",
        alt: "商品对比示意",
        caption: "用户对比测评中使用的两件商品示意",
      },
    ],
    sections: [
      {
        heading: "为什么售后拖久了更难收场",
        paragraphs: [
          "前两周是舆论的窗口期。此时给出统一方案，成本最低；拖到第四周之后，用户已经形成集体预期，任何方案都会被拿来对比。",
        ],
      },
    ],
    reactions: [
      { user: "买过同款", text: "拖了两个月才处理，属实有点慢。", likes: 2260 },
      { user: "小满未满", text: "对比视频做得挺客观的。", likes: 1340 },
    ],
  },
  {
    id: "connection-price-war",
    title: "两方主播连线比价：从互相捧场到当场翻脸",
    category: "连线失控",
    categorySlug: "connection-chaos",
    tags: ["连线失控", "比价", "名场面"],
    author: "观察局编辑 周砚",
    date: "2026-06-05",
    views: "81.6 万",
    readTime: "5 分钟",
    failIndex: 7,
    cover: "https://readdy.ai/api/search-image?query=two%20empty%20chairs%20facing%20each%20other%20on%20a%20dark%20stage%20under%20warm%20spotlights%2C%20symmetrical%20composition%2C%20no%20people%2C%20cinematic&width=1200&height=750&seq=fail-cover-10&orientation=landscape",
    excerpt: "「你先说」变成了「你别说了」。连线比价，是翻车率最高的直播形式之一。",
    lead: "比价类连线天然带有对抗性：两边的价格体系、佣金结构和库存状况往往不一致，冲突只是时间问题。",
    quote: "连线比价，比的是谁的脾气更好。",
    timeline: [
      { time: "21:00", text: "双方互相捧场，共同介绍同一款商品的优惠力度。" },
      { time: "21:18", text: "一方发现自己链接的价格高出数十元，语气开始变化。" },
      { time: "21:22", text: "双方就「谁的价更低」反复拉锯，助播多次试图打断。" },
      { time: "21:29", text: "连线被单方面挂断，直播转为单人讲解。" },
    ],
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=two%20glowing%20smartphone%20screens%20side%20by%20side%20on%20a%20dark%20table%2C%20warm%20amber%20reflections%2C%20abstract%2C%20no%20text%2C%20no%20people&width=800&height=800&seq=fail-shot-10a&orientation=squarish",
        alt: "双方连线的手机画面示意",
        caption: "连线中断前并排显示的两个直播画面",
      },
    ],
    sections: [
      {
        heading: "连线比价前必须对齐的三件事",
        paragraphs: [
          "价格是否一致、库存是否共享、佣金如何结算。这三件事没谈好就开麦，等于把冲突留到镜头前。",
          "更稳妥的做法是把比价改成「各讲卖点」，把价格交给页面呈现。",
        ],
      },
    ],
    reactions: [
      { user: "看热闹不嫌事大", text: "这段真的比综艺还好看。", likes: 4470 },
      { user: "路过的甲方", text: "第一次看到有人现场挂断的。", likes: 2050 },
    ],
  },
];

export const getArticleById = (id: string): FailArticle | undefined =>
  articles.find((item) => item.id === id);